import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

const Login = () => {
  const { login, createAdmin, loginWithToken } = useAuth();
  const { logoUrl } = useTenant();

  // Detectar token de Kontrol en URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const kt = params.get('kt');
    const tenant = params.get('tenant');
    if (kt && tenant) {
      // Auto-login con token de Kontrol
      if (loginWithToken) {
        loginWithToken(kt, tenant);
      } else {
        localStorage.setItem('token', kt);
        localStorage.setItem('tenant_id', tenant);
        window.location.href = '/';
      }
    }
  }, []);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [adminMessage, setAdminMessage] = useState('');

  const handleInputChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(credentials.username, credentials.password);
    
    if (!result.success) {
      setError(result.error);
      if (result.error.includes('Invalid username or password')) {
        setShowCreateAdmin(true);
      }
    }
    
    setLoading(false);
  };

  const handleCreateAdmin = async () => {
    setLoading(true);
    const result = await createAdmin();
    
    if (result.success) {
      setAdminMessage(`Admin creado exitosamente! 
        Usuario: admin
        Contraseña: farmavida2024`);
      setCredentials({ username: 'admin', password: 'farmavida2024' });
      setShowCreateAdmin(false);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <img 
            src={logoUrl || "/kontrol_logo.png"} 
            alt="Kontrol" 
            className="h-20 w-auto mx-auto mb-4 rounded-lg shadow-lg"
          />
          <h1 className="text-3xl font-bold text-white mb-2">Kontrol</h1>
          <p className="text-blue-200">Sistema de Gestión Financiera</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Iniciar Sesión</h2>
            <p className="text-gray-600 mt-2">Accede a tu cuenta para continuar</p>
          </div>

          {/* Admin Success Message */}
          {adminMessage && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-medium text-green-800 mb-2">✅ Administrador Creado</h4>
              <pre className="text-sm text-green-700 whitespace-pre-line">{adminMessage}</pre>
              <p className="text-xs text-green-600 mt-2">Los datos ya están pre-llenados en el formulario</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">🚨 {error}</p>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                Usuario
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={credentials.username}
                onChange={handleInputChange}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ingresa tu usuario"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={credentials.password}
                onChange={handleInputChange}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ingresa tu contraseña"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Iniciando...
                </div>
              ) : (
                'Iniciar Sesión'
              )}
            </button>
          </form>

          {/* Create Admin Section */}
          {showCreateAdmin && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-yellow-800 mb-2">🔑 Primera vez usando el sistema?</h4>
              <p className="text-sm text-yellow-700 mb-3">
                Parece que no tienes usuarios registrados. Crea el usuario administrador para comenzar.
              </p>
              <button
                onClick={handleCreateAdmin}
                disabled={loading}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-4 rounded transition duration-200"
              >
                {loading ? 'Creando...' : '👤 Crear Usuario Administrador'}
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              Kontrol © 2026 - Sistema de Gestión Financiera
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Versión 1.0 - Seguro y Confiable
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;